import { useState, useCallback, useMemo, useEffect } from "react";
import { fetchAllData } from './services/api';
import GainsCard from './components/GainsCard/GainsCard';
import HoldingsTable from './components/HoldingsTable/HoldingsTable';
import Navbar from './components/Navbar';
import type { Holding, CapitalGains } from './types';
import styles from './App.module.css';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [baseGains, setBaseGains] = useState<CapitalGains | null>(null);
  const [holdings, setHoldings] = useState<(Holding & { id: number })[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Load data from mock API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const { capitalGains, holdings: holdingsData } = await fetchAllData();
        setBaseGains(capitalGains);
        setHoldings(holdingsData.map((h, i) => ({ ...h, id: i })));
      } catch (error) {
        console.error('Failed to load data', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Set theme attribute on document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Calculate after harvesting gains based on selected holdings
  const afterGains = useMemo(() => {
    if (!baseGains) return null;

    let stcgProfits = baseGains.stcg.profits;
    let stcgLosses = baseGains.stcg.losses;
    let ltcgProfits = baseGains.ltcg.profits;
    let ltcgLosses = baseGains.ltcg.losses;

    holdings.forEach((h) => {
      if (!selectedIds.has(h.id)) return;

      if (h.stcg.gain > 0) {
        stcgProfits -= h.stcg.gain;
      } else {
        stcgLosses -= Math.abs(h.stcg.gain);
      }

      if (h.ltcg.gain > 0) {
        ltcgProfits -= h.ltcg.gain;
      } else {
        ltcgLosses -= Math.abs(h.ltcg.gain);
      }
    });

    return {
      stcg: { profits: Math.max(0, stcgProfits), losses: Math.max(0, stcgLosses) },
      ltcg: { profits: Math.max(0, ltcgProfits), losses: Math.max(0, ltcgLosses) }
    };
  }, [selectedIds, holdings, baseGains]);

  const preNet = baseGains
    ? (baseGains.stcg.profits - baseGains.stcg.losses) + (baseGains.ltcg.profits - baseGains.ltcg.losses)
    : 0;

  const afterNet = afterGains
    ? (afterGains.stcg.profits - afterGains.stcg.losses) + (afterGains.ltcg.profits - afterGains.ltcg.losses)
    : preNet;

  const savings = preNet - afterNet;

  const toggleAll = useCallback(() => {
    if (selectedIds.size === holdings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(holdings.map((h) => h.id)));
    }
  }, [selectedIds.size, holdings]);

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev: Set<number>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        Loading...
      </div>
    );
  }

  if (!baseGains) {
    return <div className={styles.errorContainer}>Error loading data</div>;
  }

  return (
    <div className={styles.mainContainer} style={{ background: isDarkMode ? 'var(--color-bg-primary)' : '#ffffff', color: isDarkMode ? 'var(--color-text-primary)' : '#111827' }}>
      <Navbar isDarkMode={isDarkMode} onThemeToggle={() => setIsDarkMode(!isDarkMode)} />
      
      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.headerRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>Tax Harvesting</h1>
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowHowItWorks((v: boolean) => !v)}
                style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 14, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}
              >
                How it works?
              </button>
              {showHowItWorks && (
                <div className="tooltip-popup" onClick={() => setShowHowItWorks(false)}>
                  <ul style={{ paddingLeft: 14 }}>
                    <li>See your capital gains for FY 2024-25 in the left card</li>
                    <li style={{ marginTop: 6 }}>Check boxes for assets you plan on selling to reduce your tax liability</li>
                    <li style={{ marginTop: 6 }}>Instantly see your updated tax liability in the right card</li>
                  </ul>
                  <p style={{ marginTop: 10, color: isDarkMode ? "#d1d5db" : "#4b5563" }}><strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>Pro tip:</strong> Experiment with different combinations of your holdings to optimize your tax liability</p>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Disclaimer Banner */}
      <div className={styles.disclaimerBanner} style={{ background: isDarkMode ? 'var(--color-bg-tertiary)' : '#f8fafc', border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #e2e8f0' }}>
        <button
          onClick={() => setDisclaimerOpen((v: boolean) => !v)}
          style={{ width: "100%", background: "none", border: "none", color: isDarkMode ? "#fff" : "#111827", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 500 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "#3b82f6", fontSize: 16 }}>ⓘ</span>
            <span>Important Notes And Disclaimers</span>
          </div>
          <span style={{ color: "#9ca3af", fontSize: 18, transform: disclaimerOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
        </button>
        {disclaimerOpen && (
          <div className={styles.disclaimerContent} style={{ color: isDarkMode ? "#d1d5db" : "#4b5563" }}>
            <p style={{ marginBottom: 8 }}><strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>Price Source Disclaimer:</strong> Please note that the current price of your coins may differ from the prices listed on specific exchanges. This is because we use <strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>CoinGecko</strong> as our default price source for certain exchanges, rather than fetching prices directly from the exchange.</p>
            <p style={{ marginBottom: 8 }}><strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>Country-specific Availability:</strong> Tax loss harvesting may <strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>not be supported in all countries</strong>. We strongly recommend consulting with your local tax advisor or accountant before performing any related actions on your exchange.</p>
            <p><strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>Utilization of Losses:</strong> Tax loss harvesting typically allows you to offset capital gains. However, if you have <strong style={{ color: isDarkMode ? "#fff" : "#111827" }}>zero or no applicable crypto capital gains</strong>, the usability of these harvested losses may be limited. Kindly confirm with your tax advisor how such losses can be applied in your situation.</p>
          </div>
        )}
      </div>

      {/* Cards Row */}
      <div className={styles.cardsRow}>
        <GainsCard variant="pre" stcg={baseGains.stcg} ltcg={baseGains.ltcg} />
        {afterGains && (
          <GainsCard variant="after" stcg={afterGains.stcg} ltcg={afterGains.ltcg} savings={savings} />
        )}
      </div>

      {/* Holdings Table */}
      <HoldingsTable
        holdings={holdings}
        selectedIds={selectedIds}
        onToggle={toggleRow}
        onToggleAll={toggleAll} isDarkMode={isDarkMode}
       
      />
      </div>
    </div>
  );
}


